import * as _ from 'underscore';
import { FilteringDataMap } from './filteringDataMap';
import { PagingDataMap } from './pagingDataMap';
import { SortingDataMap } from './sortingDataMap';
import { Event } from '..';
import { DataChangedEvent } from '../../events';
import { IEvent } from '../../interfaces';
import {
	DataSourceOptions,
	PagingOptions,
	SortOptions,
	FilterOptions
} from '../../interfaces/options';
import { DataChangedEventType } from '../../enums';
import { notify } from '../../decorators';
import { DefaultDataSourceOptions, DefaultPagingOptions } from '../../const/defaultOptions';
import { Localization } from '../../compile';

export class DataSource<T> {
	public isLoading: KnockoutObservable<boolean>;
	public data: KnockoutObservableArray<T>;
	public view: KnockoutComputed<T[]>;
	public length: KnockoutComputed<number>;

	private _get: (dataSource: DataSource<T>, ...params: any[]) => Promise<T[]>;
	private _put: (item: T) => Promise<T>;
	private _post: (item: T) => Promise<any>;
	private _delete: (item: T) => Promise<any>;
	private _deleteAll: () => Promise<any>;
	private _new: (...params: any[]) => T;
	public filteringDataMap: FilteringDataMap<T>;
	public pagingDataMap: PagingDataMap<T>;
	private sortingDataMap: SortingDataMap<T>;
	public newData: KnockoutObservableArray<T>;

	public isFilterOn: KnockoutComputed<boolean>;
	public isPagingOn: boolean;

	//Events
	private _onDataChanged: Event<DataChangedEvent<T>>;
	public get onDataChanged(): IEvent<DataChangedEvent<T>> {
		return this._onDataChanged;
	}

	constructor(options?: DataSourceOptions<T>) {
		options = $.extend(true, {}, DefaultDataSourceOptions, options || {});
		var pagingOptions: PagingOptions = options.paging
			? $.extend(true, {}, DefaultPagingOptions, options.paging || {})
			: {};

		this.isPagingOn = !_.isUndefined(pagingOptions.pageSize);
		this.isLoading = ko.observable(false);
		if (_.isFunction(options.loading)) {
			this.isLoading.subscribe((loading: boolean) => {
				options.loading(loading);
			});
		}

		this.data = options.data
			? ko.isObservable(options.data)
				? <KnockoutObservableArray<T>>options.data
				: ko.observableArray<T>(<T[]>options.data)
			: ko.observableArray<T>([]);
		this._get = _.isFunction(options.get) ? options.get : this.defaultGet;
		this._put = _.isFunction(options.put) ? options.put : this.defaultPut;
		this._post = _.isFunction(options.post) ? options.post : this.defaultPost;
		this._delete = _.isFunction(options.delete) ? options.delete : this.defaultDelete;
		this._deleteAll = _.isFunction(options.deleteAll) ? options.deleteAll : this.defaultDeleteAll;
		this._new = options.new;

		this.length = ko.pureComputed(() => {
			var _length = 0;
			var data = this.data();
			var newData = this.newData();

			_length += data ? data.length : 0;
			_length += newData ? newData.length : 0;

			return _length;
		});

		//DataMap
		this.newData = ko.observableArray<T>([]);
		this.filteringDataMap = new FilteringDataMap(this.data, options.filter);
		this.sortingDataMap = new SortingDataMap(this.filteringDataMap, options.sorting);

		let combinedData = ko.pureComputed(() => this.newData().concat(this.sortingDataMap.map()));

		this.pagingDataMap = new PagingDataMap(combinedData, pagingOptions);

		this.view = this.pagingDataMap.map;

		this.isFilterOn = ko.pureComputed(() => this.filteringDataMap.isFilterOn());

		//Events
		this._onDataChanged = new Event<DataChangedEvent<T>>();
		this.data.subscribe((newData: T[]) => {
			this._onDataChanged.notify(<DataChangedEvent<T>>{
				data: newData,
				dataSource: this,
				eventType: DataChangedEventType.CHANGED
			});
		});
	}

	public getItemBy(value: any, property: string, onlyVisible: boolean = false): T {
		if (onlyVisible) {
			return _.find(this.view(), dataItem => dataItem[property] == value);
		} else {
			return _.find(this.data(), dataItem => dataItem[property] == value);
		}
	}

	public reload(...params: any[]): Promise<DataSource<T>> {
		this.isLoading(true);

		return this._get(this, ...params)
			.then(
				(data: T[]) => {
					this.newData.removeAll();
					this.data(data);
				},
				() => {}
			)
			.then(() => {
				this.isLoading(false);
				return Promise.resolve(this);
			});
	}

	public get(...params: any[]): Promise<T[]> {
		this.isLoading(true);

		return this._get
			.apply(this, ...params)
			.then((data: T[]) => this.data(data), data => data)
			.then(() => this.isLoading(false));
	}

	@notify(() => Localization.Saved)
	public put(item: T): Promise<T> {
		this.isLoading(true);
		return this._put(item).then(item => {
			this.isLoading(false);
			return Promise.resolve(item);
		});
	}

	@notify(() => Localization.Saved)
	public post(item: T): Promise<any> {
		this.isLoading(true);
		return this._post(item).then(item => {
			this.isLoading(false);
			return Promise.resolve(item);
		});
	}

	@notify({
		value: () => Localization.Deleted,
		condition: function(item: T) {
			return !(<DataSource<T>>this).isNew(item);
		}
	})
	public delete(item: T): Promise<any> {
		if (this.isNew(item)) {
			this.newData.remove(item);
			return Promise.resolve();
		} else {
			this.isLoading(true);
			return this._delete(item).then(() => {
				this.data.remove(item);
				this.isLoading(false);
				return Promise.resolve();
			});
		}
	}

	@notify(() => Localization.DeletedAll)
	public deleteAll(): Promise<any> {
		this.isLoading(true);

		return this._deleteAll()
			.then(
				() => {
					this.newData.removeAll();
					this.data.removeAll();
				},
				() => {}
			)
			.then(() => this.isLoading(false));
	}

	@notify(() => Localization.Deleted)
	public deleteItems(items: T[]): Promise<any> {
		this.isLoading(true);
		return this._deleteItems(items)
			.then(
				() => {
					items.forEach(item => {
						if (this.isNew(item)) {
							this.newData.remove(item);
						} else {
							this.data.remove(item);
						}
					});
				},
				() => {}
			)
			.then(() => this.isLoading(false));
	}

	public new(...params: any[]) {
		let newItem: T;
		if (_.isFunction(this._new)) {
			if ((newItem = this._new.apply(this, params)) !== null && newItem !== undefined) {
				this.newData.push(newItem);
				this._onDataChanged.notify(<DataChangedEvent<T>>{
					data: [newItem],
					dataSource: this,
					eventType: DataChangedEventType.ADDED
				});
			}
		} else {
			new Error('Missing datasource.new definition.');
		}
	}

	public sync(item: T): Promise<any> {
		this.isLoading(true);
		return new Promise<any>((resolve, reject) => {
			if (this.isNew(item)) {
				this.post(item)
					.then((newItem: T) => {
						this.newData.remove(item);
						this.get()
							.then(resolve, reject)
							.then(() => this.isLoading(false));
					}, reject)
					.then(() => this.isLoading(false));
			} else
				return this.put(item)
					.then(resolve, reject)
					.then(() => this.isLoading(false));
		});
	}

	public sort(sortSettings: SortOptions[]) {
		this.sortingDataMap.sort(sortSettings);
	}

	public filter(filterSettings: FilterOptions[]) {
		this.filteringDataMap.filter(filterSettings);
	}

	public page(page: number) {
		this.pagingDataMap.page(page);
	}

	public previousPage() {
		this.pagingDataMap.previousPage();
	}

	public nextPage() {
		this.pagingDataMap.nextPage();
	}

	public firstPage() {
		this.pagingDataMap.firstPage();
	}

	public lastPage() {
		this.pagingDataMap.lastPage();
	}

	private defaultGet(dataSource: DataSource<T>, ...params: any[]): Promise<T[]> {
		var deferred = $.Deferred<T[]>();
		deferred.resolve(dataSource.data());
		return deferred.promise();
	}

	private defaultPut(item: T): Promise<T> {
		var deferred = $.Deferred<T>();
		deferred.resolve(item);
		return deferred.promise();
	}

	private defaultPost(item: T): Promise<T> {
		var deferred = $.Deferred<T>();
		deferred.resolve(item);
		return deferred.promise();
	}

	private defaultDelete(item: T): Promise<any> {
		var deferred = $.Deferred<T[]>();
		deferred.resolve();
		return deferred.promise();
	}

	private defaultDeleteAll(): Promise<any> {
		return this._deleteItems(this.data().concat(this.newData()));
	}

	private _deleteItems(items: T[]): Promise<any> {
		var deferred = $.Deferred<any>();
		if (items && items.length > 0) {
			var deleteDeferreds: Promise<any>[] = [];
			items.forEach(item => {
				if (this.isNew(item)) {
					deleteDeferreds.push(
						$
							.Deferred<any>()
							.resolve()
							.promise()
					);
				} else {
					deleteDeferreds.push(this._delete(item));
				}
			});

			$.when.apply($, deleteDeferreds).done(() => {
				deferred.resolve();
			});
		} else {
			deferred.resolve();
		}

		return deferred.promise();
	}

	public isNew(item: T): boolean {
		return this.newData.indexOf(item) >= 0;
	}
}
